import { supabase } from '../lib/supabaseClient';
import { logger } from '../utils/logger';
import { getOfflineCache, setOfflineCache } from '../lib/offlineCache';
import { isNetworkError } from '../utils/network';
import {
    WorkOrder,
    WorkOrderStatus,
    Priority,
    WorkOrderEvent,
    LaborJournalEntry,
    WorkOrderAttachment,
} from '@/types';
import type {
    UserRole,
} from '@/types'
import type {
    WorkOrderRow,
    WoEventRow,
    WoMaterialRow,
    WoLaborJournalRow,
    WoAttachmentRow,
} from '@/types/supabase';

interface DashboardStats {
    totalOrders: number
    pendingOrders: number
    inProgressOrders: number
    completedOrders: number
    urgentOrders: number
    incidentCount: number
    materialCount: number
    revenue: number
}

// Cache para dashboard stats (TTL 60s)
const _statsCache: Record<string, { data: DashboardStats; ts: number }> = {};

/** Invalida la caché de stats (ej. tras añadir incidencias/material) */
export function bustDashboardStatsCache() {
    delete _statsCache['dashboard_stats'];
}

export function mapEvents(rows: WoEventRow[]): WorkOrderEvent[] {
    return (rows || []).map((e: WoEventRow) => ({
        id: e.id,
        type: e.type as WorkOrderEvent['type'],
        text: e.text ?? '',
        timestamp: e.created_at
            ? new Date(e.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            : '',
        status: (e.status ?? 'pending') as WorkOrderEvent['status'],
    }));
}

export function mapMaterials(rows: WoMaterialRow[]): string[] {
    return (rows || []).map((m: WoMaterialRow) => `${m.material_name} (x${m.quantity})`);
}

export function mapLaborJournal(rows: WoLaborJournalRow[]): LaborJournalEntry[] {
    const seen = new Set<string>();
    const uniqueRows = (rows || []).filter((row) => {
        const key = `${row.date}_${row.main_hours}_${row.notes ?? ''}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });

    return uniqueRows.map((j: WoLaborJournalRow) => ({
        id: j.id,
        date: j.date,
        mainTechHours: j.main_hours,
        technicianId: j.technician_id,
        notes: j.notes ?? '',
        companions: (j.wo_labor_companions || []).map((c) => ({ name: c.name, hours: c.hours })),
        signature: j.signature ?? undefined,
        isAbsent: j.is_absent ?? undefined,
    }));
}

export function calcTotalHours(entries: LaborJournalEntry[]): number {
    return entries.reduce((acc, entry) => {
        const main = Number(entry.mainTechHours) || 0;
        const companions = entry.companions.reduce((sum, c) => sum + (Number(c.hours) || 0), 0);
        return acc + main + companions;
    }, 0);
}

export function mapAttachments(rows: WoAttachmentRow[]): WorkOrderAttachment[] {
    return (rows || []).map((a: WoAttachmentRow) => ({
        id: a.id,
        workOrderId: a.work_order_id,
        fileName: a.file_name,
        filePath: (a as any).file_path || '',
        fileUrl: a.file_url || '',
        contentType: a.content_type,
        size: a.size,
        uploadedBy: a.created_by ?? undefined,
        createdAt: a.created_at,
    }));
}

const mapOrderFromDB = (
    row: WorkOrderRow,
    materials: string[] = [],
    events: WorkOrderEvent[] = [],
    laborJournal: LaborJournalEntry[] = [],
    actualHours: number = 0,
    attachments: WorkOrderAttachment[] = []
): WorkOrder => ({
    id: row.visible_id,
    _uuid: row.id,
    companyId: row.company_id,
    title: row.work_sites?.name || row.title,
    description: row.description || '',
    clientId: row.client_id ?? undefined,
    clientName: row.clients?.name || 'Cliente Desconocido',
    workSiteId: row.work_site_id ?? undefined,
    rootOrderId: row.root_order_id || undefined,  // V_CHAIN: Encadenamiento de jornadas
    technicianId: row.technician_id,
    technicianNameSnapshot: row.technician_name_snapshot ?? undefined,
    status: row.status as WorkOrderStatus,
    priority: row.priority as Priority,
    scheduledDate: row.scheduled_date,
    completedAt: row.completed_at ?? undefined,
    createdAt: row.created_at,
    isArchived: row.is_archived ?? undefined,
    hiddenForTechnician: row.hidden_for_technician ?? false,
    deletedForTechnician: row.deleted_for_technician ?? false,
    location: {
        lat: row.location_lat || 0,
        lng: row.location_lng || 0,
        address: row.location_address || '',
    },
    materialsUsed: materials,
    events,
    actualHours,
    additionalLabor: laborJournal.flatMap(entry => entry.companions || []),
    laborJournal,
    attachments,
    obra: row.work_sites?.name || row.title || '',
    createdByRole: (row.created_by_role || 'admin') as UserRole,
    partNumber: row.part_number ?? null,
});

/**
 * Valida si un string es un UUID v4 canónico.
 * Si es válido, lo devuelve limpio. Si es nulo, undefined, 'unknown' o malformado, retorna null.
 */
export function toUuidOrNull(value?: string | null): string | null {
    if (!value || typeof value !== 'string') return null;
    const trimmed = value.trim();
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(trimmed) ? trimmed : null;
}

export const OrderService = {
    async getOrders(
        page: number = 1,
        limit: number = 20,
        options?: {
            isArchived?: boolean;
            searchQuery?: string;
            clientIds?: string[];
            dateRange?: { start?: string; end?: string } | null;
        }
    ): Promise<{ orders: WorkOrder[]; total: number }> {
        const cacheKey = `pd_cached_orders_${options?.isArchived ? 'archived' : 'active'}`;
        try {
            const from = (page - 1) * limit;
            const to = from + limit - 1;

            let query = supabase
                .from('work_orders')
                .select(
                    `
                    *,
                    clients (name),
                    work_sites (name),
                    wo_events (*),
                    wo_materials (*),
                    wo_labor_journal (*, wo_labor_companions (*)),
                    wo_attachments (*)
                `,
                    { count: 'exact' }
                );

            if (options && typeof options.isArchived === 'boolean') {
                query = query.eq('is_archived', options.isArchived);
            }

            if (options?.searchQuery || (options?.clientIds && options.clientIds.length > 0)) {
                const orConditions: string[] = [];
                
                if (options.searchQuery) {
                    // ILike for case-insensitive search on string fields
                    orConditions.push(`visible_id.ilike.%${options.searchQuery}%`);
                    orConditions.push(`title.ilike.%${options.searchQuery}%`);
                }
                
                if (options.clientIds && options.clientIds.length > 0) {
                    orConditions.push(`client_id.in.(${options.clientIds.join(',')})`);
                }
                
                if (orConditions.length > 0) {
                    query = query.or(orConditions.join(','));
                }
            }

            if (options?.dateRange?.start && options?.dateRange?.end) {
                query = query.gte('scheduled_date', `${options.dateRange.start}T00:00:00.000Z`)
                             .lte('scheduled_date', `${options.dateRange.end}T23:59:59.999Z`);
            } else if (options?.dateRange?.start) {
                query = query.gte('scheduled_date', `${options.dateRange.start}T00:00:00.000Z`);
            } else if (options?.dateRange?.end) {
                query = query.lte('scheduled_date', `${options.dateRange.end}T23:59:59.999Z`);
            }

            const { data, count, error } = await query
                .order('created_at', { ascending: false })
                .range(from, to);

            if (error) {
                logger.error('Error fetching orders', error);
                throw error;
            }

            const result = {
                orders: (data || []).map((row: WorkOrderRow) => {
                    const events = mapEvents(row.wo_events || []);
                    const dbMaterials = mapMaterials(row.wo_materials || []);
                    const laborJournal = mapLaborJournal(row.wo_labor_journal || []);
                    const totalHours = calcTotalHours(laborJournal);
                    const attachments = mapAttachments(row.wo_attachments || []);

                    return mapOrderFromDB(row, dbMaterials, events, laborJournal, totalHours, attachments);
                }),
                total: count || 0,
            };

            // Cache page 1 results when no filters are present
            if (page === 1 && !options?.searchQuery && (!options?.clientIds || options.clientIds.length === 0) && !options?.dateRange) {
                await setOfflineCache(cacheKey, result);
            }

            return result;
        } catch (err) {
            if (isNetworkError(err)) {
                const cached = await getOfflineCache(cacheKey);
                if (cached && page === 1 && !options?.searchQuery && (!options?.clientIds || options.clientIds.length === 0) && !options?.dateRange) {
                    logger.info(`OrderService: Restored orders from offline cache for ${cacheKey}`);
                    return cached;
                }
            }
            throw err;
        }
    },

    async getDashboardStats(forceRefresh = false): Promise<DashboardStats> {
        const CACHE_TTL_MS = 60_000;
        const cacheKey = 'dashboard_stats';
        const cached = _statsCache[cacheKey];
        if (!forceRefresh && cached && Date.now() - cached.ts < CACHE_TTL_MS) {
            return cached.data;
        }
        const result = await OrderService._fetchDashboardStats();
        _statsCache[cacheKey] = { data: result, ts: Date.now() };
        return result;
    },

    async _fetchDashboardStats(): Promise<DashboardStats> {
        const { data, error } = await supabase.rpc('get_dashboard_stats');
        if (!error && data) {
            return {
                totalOrders: data.totalOrders ?? 0,
                pendingOrders: data.pendingOrders ?? 0,
                inProgressOrders: data.inProgressOrders ?? 0,
                completedOrders: data.completedOrders ?? 0,
                urgentOrders: data.urgentOrders ?? 0,
                incidentCount: data.incidentCount ?? 0,
                materialCount: data.materialCount ?? 0,
                revenue: data.revenue ?? 0,
            };
        }
        const promises = [
            supabase.from('work_orders').select('*', { count: 'exact', head: true }),
            supabase.from('work_orders').select('*', { count: 'exact', head: true }).eq('status', WorkOrderStatus.PENDING),
            supabase.from('work_orders').select('*', { count: 'exact', head: true }).eq('status', WorkOrderStatus.IN_PROGRESS),
            supabase.from('work_orders').select('*', { count: 'exact', head: true }).eq('status', WorkOrderStatus.COMPLETED),
            supabase
                .from('work_orders')
                .select('*', { count: 'exact', head: true })
                .eq('priority', Priority.URGENT)
                .neq('status', WorkOrderStatus.COMPLETED),
            supabase
                .from('wo_events')
                .select('*', { count: 'exact', head: true })
                .eq('type', 'incident')
                .eq('status', 'pending'),
            supabase
                .from('wo_events')
                .select('*', { count: 'exact', head: true })
                .eq('type', 'material_request')
                .eq('status', 'pending'),
        ];
        const results = await Promise.all(promises);
        return {
            totalOrders: results[0].count || 0,
            pendingOrders: results[1].count || 0,
            inProgressOrders: results[2].count || 0,
            completedOrders: results[3].count || 0,
            urgentOrders: results[4].count || 0,
            incidentCount: results[5].count || 0,
            materialCount: results[6].count || 0,
            revenue: 0,
        };
    },

    async getOrderById(idOrVisibleId: string): Promise<WorkOrder | null> {
        // V228: Los IDs temporales contienen guiones pero no son UUIDs válidos.
        const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(idOrVisibleId);
        let query = supabase.from('work_orders').select(`
                *,
                clients (*),
                work_sites (*),
                technicians:technician_id (*)
            `);
        const { data: orderData, error } = await (isUuid
            ? query.eq('id', idOrVisibleId)
            : query.eq('visible_id', idOrVisibleId)
        ).single();

        if (error || !orderData) {
            logger.error('Error fetching single order', error);
            return null;
        }

        const orderId = orderData.id;

        const rootUuid = orderData.root_order_id || orderData.id;
        const { data: chainOrders } = await supabase
            .from('work_orders')
            .select('id, created_at')
            .or(`id.eq.${rootUuid},root_order_id.eq.${rootUuid}`)
            .order('created_at', { ascending: true });

        const chainIds = (chainOrders || []).map(o => o.id);
        const idsToQuery = chainIds.length > 0 ? chainIds : [orderId];

        const [materialsResult, eventsResult, journalResult, attachmentsResult] = await Promise.all([
            supabase.from('wo_materials').select('*').in('work_order_id', idsToQuery),
            supabase.from('wo_events').select('*').in('work_order_id', idsToQuery).order('created_at', { ascending: true }),
            supabase.from('wo_labor_journal').select(`*, wo_labor_companions(*)`).in('work_order_id', idsToQuery).order('date', { ascending: true }),
            supabase.from('wo_attachments').select('*').in('work_order_id', idsToQuery).order('created_at', { ascending: true })
        ]);

        const materials = mapMaterials((materialsResult.data || []).filter(m => m.work_order_id === orderId));
        const events = mapEvents((eventsResult.data || []).filter(e => e.work_order_id === orderId));
        const laborJournal = mapLaborJournal((journalResult.data || []).filter(j => j.work_order_id === orderId));
        const totalHours = calcTotalHours(laborJournal);
        const attachments = mapAttachments(attachmentsResult.data || []);

        return mapOrderFromDB(orderData, materials, events, laborJournal, totalHours, attachments);
    },

    async _resolveRootOrderUuid(rootOrderId?: string | null): Promise<string | null> {
        if (!rootOrderId || rootOrderId === '_dismissed') return null;
        const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(rootOrderId);
        if (isUuid) return rootOrderId;

        // If it's a visible_id (e.g. WO-2026-...), look up its database UUID
        const { data, error } = await supabase
            .from('work_orders')
            .select('id')
            .eq('visible_id', rootOrderId)
            .maybeSingle();

        if (error || !data?.id) {
            logger.warn(`[OrderService] Could not resolve root order UUID for visible_id "${rootOrderId}"`, error);
            return null;
        }
        return data.id;
    },

    async createOrder(order: WorkOrder): Promise<void> {
        const rootOrderUuid = await OrderService._resolveRootOrderUuid(order.rootOrderId);

        let companyId = order.companyId;
        if (!companyId) {
            const compRes = await supabase.rpc('get_my_company_id');
            companyId = compRes?.data || undefined;
        }

        const insertBuilder = supabase.from('work_orders').insert({
            visible_id: order.id,
            company_id: companyId,
            title: order.title,
            description: order.description,
            client_id: order.clientId,
            work_site_id: order.workSiteId || null,          // V_CHAIN: Persistir obra
            root_order_id: rootOrderUuid,                    // V_CHAIN: Persistir UUID de la raíz
            technician_id: order.technicianId,
            status: order.status,
            priority: order.priority,
            scheduled_date: order.scheduledDate,
            is_archived: order.isArchived,
            hidden_for_technician: order.hiddenForTechnician || false,
            deleted_for_technician: order.deletedForTechnician || false,
            location_address: order.location.address,
            created_by_id: order.technicianId, // V103: Track creator for RLS delete policy
            created_by_role: order.createdByRole || 'admin',
        });

        let newWo: { id: string; company_id?: string } | null = null;
        if (typeof insertBuilder?.select === 'function') {
            const res = await insertBuilder.select('id, company_id').single();
            if (res?.error) {
                logger.error('[OrderService] createOrder error', res.error);
                throw res.error;
            }
            newWo = res?.data || null;
        } else {
            const res = await insertBuilder;
            if (res?.error) {
                logger.error('[OrderService] createOrder error', res.error);
                throw res.error;
            }
        }

        const workOrderId = newWo?.id;
        const finalCompanyId = companyId || newWo?.company_id;

        // Persist initial Labor Journal if provided (e.g. continuations / chained orders)
        if (order.laborJournal && order.laborJournal.length > 0 && finalCompanyId && workOrderId) {
            for (const entry of order.laborJournal) {
                const { data: newJournal, error: jError } = await supabase.from('wo_labor_journal').insert({
                    work_order_id: workOrderId,
                    company_id: finalCompanyId,
                    technician_id: entry.technicianId || order.technicianId,
                    date: entry.date,
                    main_hours: Number(entry.mainTechHours) || 0,
                    notes: entry.notes || '',
                    signature: entry.signature,
                    is_absent: entry.isAbsent
                }).select('id').single();

                if (jError) {
                    logger.error('[OrderService] createOrder: error inserting labor journal', jError);
                    continue;
                }

                if (newJournal && entry.companions && entry.companions.length > 0) {
                    const compRows = entry.companions.map(c => ({
                        journal_id: newJournal.id,
                        name: c.name,
                        hours: Number(c.hours) || 0
                    }));
                    const { error: cError } = await supabase.from('wo_labor_companions').insert(compRows);
                    if (cError) logger.error('[OrderService] createOrder: error inserting companions', cError);
                }
            }
        }
    },

    async updateOrder(order: WorkOrder): Promise<void> {
        const rootOrderUuid = await OrderService._resolveRootOrderUuid(order.rootOrderId);
        const { error } = await supabase
            .from('work_orders')
            .update({
                title: order.title,
                description: order.description,
                status: order.status,
                priority: order.priority,
                technician_id: order.technicianId,
                scheduled_date: order.scheduledDate,
                location_address: order.location.address,
                work_site_id: order.workSiteId || null,     // V_CHAIN: Persistir obra
                root_order_id: rootOrderUuid,               // V_CHAIN: Persistir cadena
                is_archived: order.isArchived,
                hidden_for_technician: order.hiddenForTechnician,
                deleted_for_technician: order.deletedForTechnician,
                completed_at: order.completedAt,
                created_by_role: order.createdByRole,
            })
            .eq('visible_id', order.id);

        if (error) {
            logger.error('OrderService update error', error);
            throw error;
        }

        const { data: woData } = await supabase
            .from('work_orders')
            .select('id, company_id')
            .eq('visible_id', order.id)
            .single();

        if (!woData || !woData.id) {
            throw new Error(`No se encontró la orden ${order.id} o no tienes permisos.`);
        }

        const workOrderId = woData.id;
        const companyId = order.companyId ?? woData.company_id;

        // 1. Update Events
        const events = order.events ?? [];
        const newEvents = events.filter((e) => typeof e.id === 'string' && e.id.startsWith('ev-'));
        if (newEvents.length > 0 && companyId) {
            const evRows = newEvents.map((e) => ({
                work_order_id: workOrderId,
                company_id: companyId,
                type: e.type,
                text: e.text ?? '',
                status: e.status ?? 'pending',
            }));
            const { error: evError } = await supabase.from('wo_events').insert(evRows);
            if (evError) {
                logger.error('OrderService: error inserting events', evError);
                // V2.2: We don't throw to avoid blocking the whole update, but it's now logged more clearly
            }
        }

        // 2. Update Materials
        if (order.materialsUsed && companyId) {
            await supabase.from('wo_materials').delete().eq('work_order_id', workOrderId);
            if (order.materialsUsed.length > 0) {
                const matRows = order.materialsUsed.map(m => {
                    const match = m.match(/(.*)\s\(x(.*)\)/);
                    return {
                        work_order_id: workOrderId,
                        company_id: companyId,
                        material_name: match ? match[1].trim() : m,
                        quantity: match ? parseInt(match[2], 10) || 1 : 1
                    };
                });
                const { error: matError } = await supabase.from('wo_materials').insert(matRows);
                if (matError) logger.error('OrderService: error inserting materials', matError);
            }
        }

        // 3. Update Labor Journal (Upsert Idempotente con garantía matemática en PostgreSQL)
        if (order.laborJournal && companyId) {
            // Desduplicar journals por fecha (la última entrada gana)
            const journalsByDate = new Map<string, LaborJournalEntry>();
            for (const entry of order.laborJournal) {
                if (entry.date) {
                    journalsByDate.set(entry.date, entry);
                }
            }
            const deduplicatedJournals = Array.from(journalsByDate.values());

            for (const entry of deduplicatedJournals) {
                const safeTechId = toUuidOrNull(entry.technicianId) ?? toUuidOrNull(order.technicianId);
                const journalPayload = {
                    work_order_id: workOrderId,
                    company_id: companyId,
                    technician_id: safeTechId,
                    date: entry.date,
                    main_hours: Number(entry.mainTechHours) || 0,
                    notes: entry.notes || '',
                    signature: entry.signature,
                    is_absent: entry.isAbsent ?? false
                };

                const { data: upsertedJournal, error: jError } = await supabase
                    .from('wo_labor_journal')
                    .upsert(journalPayload, { onConflict: 'work_order_id,date' })
                    .select('id')
                    .single();

                if (jError) {
                    logger.error('OrderService: error inserting labor journal', jError);
                    if (entry.signature) {
                        throw new Error(`Error al persistir la firma del parte de trabajo: ${jError.message || 'Fallo en wo_labor_journal'}`);
                    }
                    continue;
                }

                if (upsertedJournal && entry.companions) {
                    // Reemplazar companions de esta jornada de forma atómica y aislada
                    await supabase.from('wo_labor_companions').delete().eq('journal_id', upsertedJournal.id);
                    if (entry.companions.length > 0) {
                        const compRows = entry.companions.map(c => ({
                            journal_id: upsertedJournal.id,
                            name: c.name,
                            hours: Number(c.hours) || 0
                        }));
                        const { error: cError } = await supabase.from('wo_labor_companions').insert(compRows);
                        if (cError) logger.error('OrderService: error inserting companions', cError);
                    }
                }
            }
        }

    },

    async deleteEvent(eventId: string): Promise<void> {
        const { data, error } = await supabase.from('wo_events').delete().eq('id', eventId).select('id');
        if (error) throw error;
        if (!data || data.length === 0) {
            throw new Error('No se pudo eliminar el evento. Puede que ya no exista o no tengas permisos.');
        }
    },

    async updateEvent(eventId: string, newText: string): Promise<void> {
        const { error } = await supabase.from('wo_events').update({ text: newText }).eq('id', eventId);
        if (error) throw error;
    },

    async deleteOrder(orderId: string): Promise<void> {
        const { error } = await supabase.from('work_orders').delete().eq('visible_id', orderId);
        if (error) throw error;
    },

    async uploadAttachment(workOrderVisibleId: string, file: File | Blob, fileName: string): Promise<WorkOrderAttachment> {
        if (!file || file.size === 0) {
            throw new Error("El archivo seleccionado está vacío o no se ha podido leer del dispositivo (0 bytes).");
        }

        // 1. Resolve UUID and company_id from visible_id
        const { data: woData, error: woError } = await supabase
            .from('work_orders')
            .select('id, company_id')
            .eq('visible_id', workOrderVisibleId)
            .single();
        if (woError || !woData) throw new Error("Work order not found");
        const workOrderId = woData.id;
        const companyId = woData.company_id;

        // 2. Read bytes into in-memory ArrayBuffer to guarantee explicit Content-Length
        // and eliminate iOS WebKit / Safari 0-byte multipart FormData upload errors
        const arrayBuffer = typeof file.arrayBuffer === 'function'
            ? await file.arrayBuffer()
            : await new Promise<ArrayBuffer>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result as ArrayBuffer);
                reader.onerror = reject;
                reader.readAsArrayBuffer(file);
            });

        if (!arrayBuffer || arrayBuffer.byteLength === 0) {
            throw new Error("No se pudieron leer los datos de la imagen desde el dispositivo (0 bytes).");
        }

        const mimeType = file.type || 'image/jpeg';
        const sanitizedFileName = (fileName || 'foto.jpg').replace(/[^a-zA-Z0-9._-]/g, '_');
        const filePath = `${companyId}/${workOrderId}/${Date.now()}_${sanitizedFileName}`;

        const { error: uploadError } = await supabase.storage
            .from('work_order_attachments')
            .upload(filePath, arrayBuffer, { 
                cacheControl: '3600', 
                contentType: mimeType,
                upsert: false 
            });

        if (uploadError) throw uploadError;

        // 3. Generate ephemeral signed URL (1 hour / 3600s)
        let signedUrl = '';
        const { data: signedData } = await supabase.storage
            .from('work_order_attachments')
            .createSignedUrl(filePath, 3600);
        if (signedData?.signedUrl) {
            signedUrl = signedData.signedUrl;
        }

        // 4. Get active user session to populate created_by (V2233)
        const { data: userData } = await supabase.auth.getUser();
        const userId = userData.user?.id || null;

        // 5. Save metadata to wo_attachments (file_url is null, file_path is source of truth)
        const { data: attachmentData, error: dbError } = await supabase.from('wo_attachments').insert({
            work_order_id: workOrderId,
            file_name: fileName,
            file_path: filePath,
            file_url: null,
            content_type: mimeType,
            size: arrayBuffer.byteLength,
            created_by: userId
        }).select().single();

        if (dbError) {
            // Rollback storage if db fails
            await supabase.storage.from('work_order_attachments').remove([filePath]);
            throw dbError;
        }

        return {
            id: attachmentData.id,
            workOrderId: attachmentData.work_order_id,
            fileName: attachmentData.file_name,
            filePath: attachmentData.file_path,
            fileUrl: signedUrl,
            contentType: attachmentData.content_type,
            size: attachmentData.size,
            uploadedBy: attachmentData.created_by,
            createdAt: attachmentData.created_at,
        };
    },

    async getSignedAttachmentUrl(filePath: string, expiresIn = 3600): Promise<string> {
        if (!filePath) return '';
        const { data, error } = await supabase.storage
            .from('work_order_attachments')
            .createSignedUrl(filePath, expiresIn);
        if (error || !data?.signedUrl) {
            throw error || new Error('Could not create signed URL');
        }
        return data.signedUrl;
    },

    async resolveAttachmentUrls(attachments: WorkOrderAttachment[] = []): Promise<WorkOrderAttachment[]> {
        if (!attachments || attachments.length === 0) return [];
        return Promise.all(
            attachments.map(async (att) => {
                if (att.fileUrl && att.fileUrl.startsWith('http') && !att.fileUrl.includes('placeholder')) {
                    return att;
                }
                if (att.filePath) {
                    try {
                        const signedUrl = await OrderService.getSignedAttachmentUrl(att.filePath, 3600);
                        return { ...att, fileUrl: signedUrl };
                    } catch (err) {
                        console.warn('[OrderService] Could not sign url for attachment:', att.id, err);
                    }
                }
                return att;
            })
        );
    },

    async deleteAttachment(attachmentId: string): Promise<void> {
        // 1. Get file path
        const { data: attachment } = await supabase.from('wo_attachments').select('file_path').eq('id', attachmentId).single();
        if (!attachment) return;

        // 2. Delete from storage
        if (attachment.file_path) {
            const { error: storageError } = await supabase.storage.from('work_order_attachments').remove([attachment.file_path]);
            if (storageError) throw storageError;
        }

        // 3. Delete from table
        const { error: dbError } = await supabase.from('wo_attachments').delete().eq('id', attachmentId);
        if (dbError) throw dbError;
    }
};
